// Auto-generated meta about each module screen, extracted from source at
// build/dev time via Vite's import.meta.glob (raw). Feeds SourceCard.
//
// This is regex-based, not AST-based — kept intentionally simple. If a
// rename or refactor moves references outside the patterns below, the card
// silently misses them. The card carries a "generated on" indicator so
// devs can tell how fresh the data is.
//
// Anchor: each route in the app maps to a "root component file" (the file
// that renders the top-level screen for that route). The index walks each
// root and produces:
//   - relative path
//   - Zustand store fields read
//   - Lens API method names called (api.<name>)
//   - Stack API method names called (stackApi.<name>)
//   - HTTP-shaped API paths passed to api("METHOD", "/path", ...)
//   - imports of other src/lib/api/* clients (data-model touchpoints)

export interface RouteMeta {
  route: string;               // wouter path pattern
  file: string;                // repo-relative source path
  storeReads: string[];        // useAppStore((s) => s.field) fields
  lensApiCalls: string[];      // /api/... paths hit through api()
  stackApiCalls: string[];     // stackApi.<method>()
  apiClientImports: string[];  // src/lib/api/<name>
  githubUrl: string;           // link to file on GitHub main
}

// ── Route → root file mapping ─────────────────────────────────────────────
// Keep this in sync with App.tsx's <Route> table. Anything not listed here
// falls through to a blank SourceCard.
const ROUTE_ROOTS: { route: string; file: string }[] = [
  { route: "/",                       file: "src/components/dashboard/Dashboard.tsx" },
  { route: "/consultants",            file: "src/components/consultants/ConsultantsModule.tsx" },
  { route: "/consultants/:folder",    file: "src/components/consultants/ConsultantDossier.tsx" },
  { route: "/customers",              file: "src/components/customers/CustomersScreen.tsx" },
  { route: "/matrix",                 file: "src/components/admin/AdminDashboard.tsx" },
  { route: "/vacancies",              file: "src/components/vacancies/VacancyLibrary.tsx" },
  { route: "/vacancies/new",          file: "src/components/vacancies/VacancyEvaluator.tsx" },
  { route: "/workflows/:id",          file: "src/components/workflows/WorkflowWorkspace.tsx" },
  { route: "/consolidation",          file: "src/components/consolidation/ConsolidationScreen.tsx" },
  { route: "/stack",                  file: "src/components/stack/StackScreen.tsx" },
  { route: "/settings",               file: "src/components/settings/SettingsScreen.tsx" },
  { route: "/profile",                file: "src/components/profile/ConsultantProfile.tsx" },
];

const GITHUB_BASE = "https://github.com/jarppi/Lens/blob/main/";

// Vite: load only the routed root files as raw text at build/dev time.
// Kept narrow so we're not shipping every component's source to the client.
// If you add a screen to ROUTE_ROOTS above, add its file below too.
const RAW_MODULES = import.meta.glob([
  "../../components/dashboard/Dashboard.tsx",
  "../../components/consultants/ConsultantsModule.tsx",
  "../../components/consultants/ConsultantDossier.tsx",
  "../../components/customers/CustomersScreen.tsx",
  "../../components/admin/AdminDashboard.tsx",
  "../../components/vacancies/VacancyLibrary.tsx",
  "../../components/vacancies/VacancyEvaluator.tsx",
  "../../components/workflows/WorkflowWorkspace.tsx",
  "../../components/consolidation/ConsolidationScreen.tsx",
  "../../components/stack/StackScreen.tsx",
  "../../components/settings/SettingsScreen.tsx",
  "../../components/profile/ConsultantProfile.tsx",
], { query: "?raw", import: "default", eager: true }) as Record<string, string>;

function keyForFile(file: string): string {
  // file = "src/components/foo/bar.tsx"
  // glob key = "../../components/foo/bar.tsx"
  return file.replace(/^src\//, "../../");
}

function extract(source: string): Omit<RouteMeta, "route" | "file" | "githubUrl"> {
  const uniq = <T,>(xs: T[]): T[] => Array.from(new Set(xs));

  // useAppStore((s) => s.field)  |  useAppStore(s => s.field)
  const storeReads = uniq(
    [...source.matchAll(/useAppStore\s*\(\s*\(?s\)?\s*=>\s*s\.([A-Za-z_][A-Za-z0-9_]*)/g)]
      .map((m) => m[1]!)
  ).sort();

  // stackApi.<method>(
  const stackApiCalls = uniq(
    [...source.matchAll(/\bstackApi\.([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)].map((m) => m[1]!)
  ).sort();

  // api("METHOD", "/path…", …)  — imported from ../api/_client (or transitively via a domain client).
  // We can't easily follow transitive calls without AST, but domain clients import
  // "./_client" and every call is api("METHOD", `/api/...`, ...). Catch either the
  // top-level call or the string literal path passed anywhere.
  const lensApiCalls = uniq(
    [...source.matchAll(/["'`](\/api\/[A-Za-z0-9_\-\/\{\}\?\=\&\.:]+)["'`]/g)].map((m) => m[1]!)
  )
    .filter((path) => !path.startsWith("/api/ai/"))   // AI proxy is a distinct concern
    .sort();

  // Imports of src/lib/api/<name> — the data-model touchpoints.
  const apiClientImports = uniq(
    [...source.matchAll(/from\s+["'`](?:\.\.\/)+lib\/api\/([A-Za-z_][A-Za-z0-9_\-]*)["'`]/g)]
      .map((m) => m[1]!)
  )
    .filter((name) => name !== "_client")
    .sort();

  return { storeReads, stackApiCalls, lensApiCalls, apiClientImports };
}

const ROUTE_META: RouteMeta[] = ROUTE_ROOTS.map(({ route, file }) => {
  const raw = RAW_MODULES[keyForFile(file)] ?? "";
  const extracted = extract(raw);
  return {
    route,
    file,
    githubUrl: GITHUB_BASE + file,
    ...extracted,
  };
});

// Route matcher — mirrors the longest-prefix logic used by module-map.ts.
export function metaForPath(pathname: string): RouteMeta | null {
  // Exact-match single-segment / parameterised routes first
  const exact = ROUTE_META.find((m) => matchesPattern(m.route, pathname));
  return exact ?? null;
}

function matchesPattern(pattern: string, pathname: string): boolean {
  // Convert "/consultants/:folder" → "^/consultants/[^/]+$"
  const regex = new RegExp(
    "^" + pattern.replace(/:[A-Za-z_][A-Za-z0-9_]*/g, "[^/]+") + "$"
  );
  return regex.test(pathname);
}

// Also expose the full list so a debug panel or future overview can list them.
export function allRouteMeta(): RouteMeta[] {
  return ROUTE_META;
}
